import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { ShieldCheck, Trophy, Building2, Globe2 } from "lucide-react";
import AppLayout from "../components/AppLayout";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

export default function Federation() {
  const { user } = useAuth();
  const [data, setData] = useState({ rankings: [], verified_tournaments: [], verified_leagues: [], affiliated_clubs: [] });
  const [tournaments, setTournaments] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [clubs, setClubs] = useState([]);
  const fetchAll = useCallback(async () => {
    const [{ data: ov }, { data: tr }, { data: lg }, { data: cl }] = await Promise.all([
      api.get("/federation/overview"),
      api.get("/tournaments"),
      api.get("/leagues"),
      api.get("/clubs"),
    ]);
    setData(ov); setTournaments(tr); setLeagues(lg); setClubs(cl);
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const verify = async (kind, id) => {
    try { await api.post(`/federation/verify/${kind}/${id}`); toast.success(`${kind} verified`); fetchAll(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const isFedOrAdmin = user && (user.role === "federation" || user.role === "admin");

  return (
    <AppLayout>
      <h1 className="font-heading text-3xl sm:text-4xl font-black flex items-center gap-3"><Globe2 size={28} className="text-[#00E676]" /> Federation</h1>
      <p className="text-zinc-400 mt-2">Official rankings, verified events and affiliated clubs.</p>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="pp-card-elevated p-6 lg:col-span-1">
          <h3 className="font-heading text-xl font-bold flex items-center gap-2"><Trophy size={18} className="text-[#00E676]" /> Top 20 (ELO)</h3>
          <table className="w-full mt-4 text-sm" data-testid="fed-rankings">
            <thead className="text-xs uppercase tracking-wider text-zinc-500"><tr><th className="text-start py-2">#</th><th className="text-start">Player</th><th className="text-end">ELO</th></tr></thead>
            <tbody>
              {data.rankings.map((p, i) => (
                <tr key={p.id} className="border-t border-white/5">
                  <td className="py-2 text-zinc-500">{i + 1}</td>
                  <td>{p.first_name} {p.last_name} <span className="text-zinc-500 text-xs">{p.country || ""}</span></td>
                  <td className="text-end font-bold text-[#00E676]">{p.elo}</td>
                </tr>
              ))}
              {data.rankings.length === 0 && <tr><td colSpan={3} className="py-6 text-center text-zinc-500">No players yet.</td></tr>}
            </tbody>
          </table>
        </div>

        <div className="lg:col-span-2 space-y-6">
          <Section title="Tournaments" icon={<Trophy size={18} />}>
            {tournaments.map((t) => (
              <Row key={t.id} verified={t.is_verified} data-testid={`fed-tour-${t.id}`}>
                <Link to={`/tournaments/${t.id}`} className="font-bold hover:text-[#00E676]">{t.title}</Link>
                <span className="text-xs text-zinc-500">{t.sport?.name}</span>
                {isFedOrAdmin && !t.is_verified && (
                  <button onClick={() => verify("tournament", t.id)} data-testid={`verify-tour-${t.id}`} className="pp-btn-secondary px-3 py-1 rounded-lg text-xs ms-auto">Verify</button>
                )}
              </Row>
            ))}
          </Section>

          <Section title="Leagues" icon={<Trophy size={18} />}>
            {leagues.map((l) => (
              <Row key={l.id} verified={l.is_verified} data-testid={`fed-league-${l.id}`}>
                <Link to={`/leagues/${l.id}`} className="font-bold hover:text-[#00E676]">{l.title}</Link>
                <span className="text-xs text-zinc-500">{l.sport?.name}</span>
                {isFedOrAdmin && !l.is_verified && (
                  <button onClick={() => verify("league", l.id)} data-testid={`verify-league-${l.id}`} className="pp-btn-secondary px-3 py-1 rounded-lg text-xs ms-auto">Verify</button>
                )}
              </Row>
            ))}
          </Section>

          <Section title="Clubs" icon={<Building2 size={18} />}>
            {clubs.map((c) => (
              <Row key={c.id} verified={c.is_verified} data-testid={`fed-club-${c.id}`}>
                <Link to={`/clubs/${c.id}`} className="font-bold hover:text-[#00E676]">{c.name}</Link>
                <span className="text-xs text-zinc-500">{c.city} · {c.country}</span>
                {isFedOrAdmin && !c.is_verified && (
                  <button onClick={() => verify("club", c.id)} data-testid={`verify-club-${c.id}`} className="pp-btn-secondary px-3 py-1 rounded-lg text-xs ms-auto">Verify</button>
                )}
              </Row>
            ))}
          </Section>
        </div>
      </div>
    </AppLayout>
  );
}

function Section({ title, icon, children }) {
  return (
    <div className="pp-card p-6">
      <h3 className="font-heading text-xl font-bold flex items-center gap-2 text-[#00E676]">{icon} {title}</h3>
      <div className="mt-3 space-y-2">{children}</div>
    </div>
  );
}
function Row({ children, verified, ...rest }) {
  return (
    <div className="flex items-center gap-3 px-3 py-2 bg-[#131313] border border-white/5 rounded-lg" {...rest}>
      {verified ? <ShieldCheck size={14} className="text-[#00E676]" /> : <ShieldCheck size={14} className="text-zinc-700" />}
      {children}
    </div>
  );
}
